import { GamePlace, DropCardPlace } from "constants/cards";
import { DROP_Z_INDEX, Displays } from "constants/layout";
import { DEAL_DELAY } from "constants/move";
import { BaseGameView } from "game/strategies/BaseStrategy";
import { SolitaireGame } from "game/SolitaireGame";
import { Point } from "pixi.js";
import { Card } from "./Card";
import { Column } from "game/Column";
import { Foundation } from "game/Foundation";
import { Layout } from "game/LayoutManager";
import { refreshPyramidCards } from "game/utils/pyramid";
import { Store } from "game/store/gameStore";
import { getScreenSize } from "game/core/screen";

export class PyramidView extends BaseGameView {
    public static readonly pyramidRows: number = 7;

    solitaireGame?: SolitaireGame;

    settingsTweens: gsap.core.Tween[];

    constructor(solitaireGame?: SolitaireGame) {
        super();
        this.solitaireGame = solitaireGame;
        this.settingsTweens = [];
    }


    draw(cards: string[], onComplete: () => void) {
        if (!this.solitaireGame) return;
        console.log('[PYRAMID] Starting pyramid initialization with', cards.length, 'cards');
    

        this.solitaireGame.stockPile?.repositionPlaceholder();
        this.solitaireGame.stockPile?.drawPlaceholder();

        
        const foundations = Layout.foundations;
        console.log('[PYRAMID] Creating', foundations, 'foundations');

        for (let x = 0; x < foundations; x++) {
            const foundation = new Foundation(this.solitaireGame, x);
            this.solitaireGame.foundations.push(foundation);
        }
        
        this.solitaireGame.columns = [];
        for (let row = 0; row < PyramidView.pyramidRows; row++) {
            const column = new Column(this.solitaireGame, row);
            column.hidePlaceholder();
            this.solitaireGame.columns.push(column);
        }
        
        cards.forEach((c) => {
            const newCard = new Card(c);
            this.solitaireGame?.cards.push(newCard);
            this.solitaireGame?.stockPile.addCard(newCard);
            this.solitaireGame?.gameContainer.addChild(newCard.container);
        });
        
        let cardIndex = 0;

        for (let row = 0; row < PyramidView.pyramidRows; row++) {
            for (let col = 0; col <= row; col++) {
                const card = this.solitaireGame.stockPile.cards.shift();
                
                if (!card) {
                    console.warn('[PYRAMID] No card available for position row:', row, 'col:', col);
                    continue;
                }
                
                const pos = this.getCardPosition(row, col);
                
                card.setPlace({
                    cardPlace: GamePlace.TABLEAU,
                    value: row,
                    zIndex: DROP_Z_INDEX + row * 10 + col,
                    position: pos,
                    order: col,
                });
                
                card.container.position.set(pos.x, pos.y);
                card.container.zIndex = DROP_Z_INDEX + row * 10 + col;
                card.visible = true;
                
                this.solitaireGame.columns[row].addCard(card);
                
                setTimeout(() => {
                    card.setVisible(true, true);
                    card.cardDisplay.castShadow(true);
                }, cardIndex * DEAL_DELAY * 1000 + 100);
                
                card.setActive(false);
                console.log(`[PYRAMID] Placed card: row:${row} col:${col} code:${card.code} value:${card.value} active:${card.active} visible:${card.visible} zIndex:${card.container.zIndex}`);
                cardIndex++;
            }
        }
        
        this.solitaireGame.stockPile.unblock();
        
        refreshPyramidCards(this.solitaireGame);

        setTimeout(() => {
            onComplete();
        }, cardIndex * DEAL_DELAY * 1000 + 200);
    }

    getCardPosition(row: number, col: number): Point {
        const cardSize = Layout.getCardSize();

        const startY = Layout.getPyramidTopPadding();

        const startX = 0;

        const x = startX + (col - row / 2) * (cardSize.width + (cardSize.width * 0.1)) + Layout.getPyramidXOffset();
        const y = startY + row * (cardSize.height * 0.4);
        return new Point(x, y);
    }

    resize(): void {
        this.reDraw();
    }

    reDraw(): void {
        if (!this.solitaireGame) return;

        if (this.solitaireGame.cards.length < PyramidView.pyramidRows) {
            return;
        }
        
        let index = 0;

        for (let row = 0; row < PyramidView.pyramidRows; row++) {
            const column = this.solitaireGame.columns[row];
            column.hidePlaceholder();

            for (let col = 0; col <= row; col++) {
                const card = column.cards[col];
                index++;

                if (!card) {
                    console.log('[PYRAMID] No card available for position row:', row, 'col:', col);
                    continue;
                }
                
                const pos = this.getCardPosition(row, col);
                
                card.setPlace({
                    cardPlace: GamePlace.TABLEAU,
                    value: row, // row как индекс колонки
                    zIndex: DROP_Z_INDEX + row * 10 + col,
                    position: pos,
                    order: col,
                });
                
                card.container.position.set(pos.x, pos.y);

                
                console.log(`[PYRAMID] Placed card: row:${row} col:${col} code:${card.code} value:${card.value} active:${card.active} visible:${card.visible} zIndex:${card.container.zIndex}`);
            }
        }

        this.solitaireGame.cards.forEach(card => {
            card?.cardDisplay?.redraw();
        });
    }

    getTableSize(): number {
        const { height, width } = getScreenSize();

        if (Layout.IsHorizontalOrientationMobile) {
            return (height * (90 * (width / height))) / 470;
        }

        if (Layout.IsVerticalOrientationMobile) {
            return (height * (90 * (width / height))) / 900;
        }

        if (Layout.getDisplay() === Displays.DESKTOP) {
            return (height * (130 * (width / height))) / 900;
        }

        if (Layout.getDisplay() === Displays.TABLET) {
            return (height * (90 * (width / height))) / 400;
        }

        return 0;
    }

    getWastePalePosition(cardGap: number): Point {
        const { width } = Layout.getCardSize();
        const stockPilePosition = Layout.getStockPilePosition();
        return new Point(Store.leftHanded ? 
            stockPilePosition.x + width + cardGap + (width * 0.2) 
            : stockPilePosition.x - width + cardGap - (width * 0.2), stockPilePosition.y);
    }

    getFoundationPosition(): Point {
        const { height } = getScreenSize();
        const screenBottom = (height / 2) + Layout.getCardSize().height;
        return new Point(0, screenBottom);
    }

    getAvailableClickPlace(): DropCardPlace[] {
        if (!this.solitaireGame) return [];

        const places: DropCardPlace[] = [];
        for (let index = 0; index < this.solitaireGame.foundations.length; index++) {
            places.push({ place: GamePlace.FOUNDATION, dropColumnIndex: index });
        }
        console.log(`[PYRAMID CLICK] Found ${places.length} click places for pyramid`);
        return places;
    }
}